import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
}                           from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  HaynesProVesaComponentImagesModel,
  HaynesProVesaDiagnosisGroupModel,
  HaynesProVesaDiagnosisStepModel,
  HaynesProVesaPinConnectionComponentModel,
  HaynesProVesaPinConnectionModel,
  HaynesProVesaPinConnectionRowModel
} from '../typings/haynes-pro.vesa-advanced';

import { HaynesProSvgInteractionDirective } from '../../../shared/haynes-pro.svg-interaction.directive';
import { Table }                            from '../../../../table/types/table';
import { TableRowEvent }                    from '../../../../table/types/table-row-event';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa-wiring-diagram',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-wiring-diagram.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-wiring-diagram.component.html')
})
export class HaynesProDiagnosticToolVesaWiringDiagramComponent
  extends HaynesProSvgInteractionDirective
  implements AfterViewInit, OnChanges, OnDestroy {

  @Input() diagnosisGroups : Array<HaynesProVesaDiagnosisGroupModel>        = [];
  @Input() images          : HaynesProVesaComponentImagesModel | undefined;
  @Input() pinConnections  : Array<HaynesProVesaPinConnectionModel>         = [];

  @Output() componentNavigate = new EventEmitter<{ componentId: string; systemTypeId: string }>();

  @ViewChild('svgContainer', { static: false }) svgContainer : ElementRef<HTMLDivElement> | undefined;
  @ViewChild('zoomViewport', { static: false }) zoomViewport : ElementRef<HTMLDivElement> | undefined;

  currentGroupIndex  = 0;
  diagnosisPanelOpen = true;
  expandedStepIndex  = -1;
  highlightedCode    : string | null = null;
  isExtendedView     = false;
  loadingSvg         = false;
  panX               = 0;
  panY               = 0;
  svgBaseHeight      = 0;
  svgBaseWidth       = 0;
  svgError           = '';
  zoomLevel          = 1;

  pinTableRows      : Array<HaynesProVesaPinConnectionRowModel> = [];
  pinTableSettings !: Table;
  stepAnswerChain   : Array<HaynesProVesaDiagnosisStepModel>    = [];
  steps             : Array<HaynesProVesaDiagnosisStepModel>    = [];

  private componentCodeMap    : Map<string, HaynesProVesaPinConnectionComponentModel>     = new Map();
  private dragMouseDownBound ?: ( event : MouseEvent ) => void;
  private dragMouseMoveBound ?: ( event : MouseEvent ) => void;
  private dragMouseUpBound   ?: ( event : MouseEvent ) => void;
  private dragRafId           : number | null                                             = null;
  private originalSvgViewBox  : string | null                                             = null;
  private svgCodeToAnchorData : Map<string, { componentId: string; tooltipText: string }> = new Map();
  private svgComponentAnchors : Map<string, Element>                                      = new Map();
  private tooltipEl          ?: HTMLDivElement;
  private tooltipMouseMove   ?: ( event : MouseEvent ) => void;
  private tooltipMouseOut    ?: ( event : MouseEvent ) => void;
  private tooltipMouseOver   ?: ( event : MouseEvent ) => void;
  private wheelListenerBound ?: ( event : WheelEvent ) => void;

  private dragLatestX    = 0;
  private dragLatestY    = 0;
  private dragScrollLeft = 0;
  private dragScrollTop  = 0;
  private dragStartX     = 0;
  private dragStartY     = 0;
  private isDragging     = false;

  private readonly DRAG_THRESHOLD = 5;
  private readonly SVG_NS        = 'http://www.w3.org/2000/svg';
  private readonly SVG_XLINK     = 'http://www.w3.org/1999/xlink';
  private readonly VESA_ASSETS   = 'assets/images/vesa/';
  private readonly ZOOM_STEPS    : Array<number> = [
    0.5,  0.75, 1,    1.25, 1.5,  1.75,
    2,    2.25, 2.5,  2.75, 3,    3.5,
    4,    4.5,  5
  ];

  constructor ( public translateService : TranslateService ) {
    super();
  }

  ngAfterViewInit () : void {
    setTimeout(() => {
      this.loadWiringSvg();
      this.bindWheelZoom();
      this.bindDragPan();
    });
  }

  ngOnDestroy () : void {
    this.clearStepOverlay();

    const viewport = this.zoomViewport?.nativeElement;

    if (viewport && this.wheelListenerBound) {
      viewport.removeEventListener('wheel', this.wheelListenerBound);
      this.wheelListenerBound = undefined;
    }

    if (viewport && this.dragMouseDownBound) {
      viewport.removeEventListener('mousedown', this.dragMouseDownBound);
      this.dragMouseDownBound = undefined;
    }

    if (this.dragMouseMoveBound) {
      document.removeEventListener('mousemove', this.dragMouseMoveBound);
      this.dragMouseMoveBound = undefined;
    }

    if (this.dragMouseUpBound) {
      document.removeEventListener('mouseup', this.dragMouseUpBound);
      this.dragMouseUpBound = undefined;
    }

    if (this.dragRafId !== null) {
      cancelAnimationFrame(this.dragRafId);
      this.dragRafId = null;
    }

    if (this.tooltipEl?.parentNode) {
      this.tooltipEl.parentNode.removeChild(this.tooltipEl);
      this.tooltipEl = undefined;
    }

    this.tooltipMouseOver = undefined;
    this.tooltipMouseOut  = undefined;
    this.tooltipMouseMove = undefined;
  }

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.diagnosisGroups || changes.images || changes.pinConnections) {
      this.init();

      if (!changes.images?.isFirstChange()) {
        setTimeout(() => this.loadWiringSvg());
      }
    }
  }

  protected getInteractiveItemIds () : Array<number> {
    return [];
  }

  protected getMatchedSvgElements ( _itemId : number ) : Array<Element> {
    return [];
  }

  protected getHoveredItemId () : number | null {
    return null;
  }

  protected getSelectedItemId () : number | null {
    return null;
  }

  protected handleResolvedSvgClick ( _itemId : number ) : void {
    // Not used but required
  }

  protected setHoveredItemId ( _itemId : number | null ) : void {
    // Not used but required
  }

  get currentGroupTitle () : string {
    return this.diagnosisGroups?.[this.currentGroupIndex]?.title || '';
  }

  get diagnosisCounter () : string {
    if (!this.steps?.length) {
      return '';
    }

    const current = this.expandedStepIndex >= 0 ? this.expandedStepIndex + 1 : 0;

    return current + '/' + this.steps.length;
  }

  get hasDiagnosis () : boolean {
    return this.steps?.length > 0;
  }

  get stageTransform () : string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`;
  }

  get viewToggleKey () : string {
    return this.isExtendedView
      ? 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.CONCISE_WIRING_DIAGRAM'
      : 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.EXTENDED_WIRING_DIAGRAM';
  }

  get zoomPercent () : number {
    return Math.round(this.zoomLevel * 100);
  }

  answerStep ( answer : 'yes' | 'no' ) : void {
    const currentQuestion = this.stepAnswerChain.length
      ? this.stepAnswerChain[this.stepAnswerChain.length - 1]
      : this.steps[this.expandedStepIndex];

    if (!currentQuestion || currentQuestion.type !== 'yes/no question') {
      return;
    }

    const nextStep = answer === 'yes' ? currentQuestion.yes_answer : currentQuestion.no_answer;

    if (!nextStep) {
      return;
    }

    this.stepAnswerChain.push(nextStep);
    this.clearStepOverlay();

    // If the answer is itself a question, show its multimeter overlay
    if (nextStep.type === 'yes/no question' && (nextStep.tool || nextStep.flash_vars)) {
      this.showStepOverlay(nextStep);
    }
  }

  formatStepText ( text : string ) : string {
    if (!text) {
      return '';
    }

    return text.replace(
      /\[component\s+si=(\d+),st=(\d+)[^\]]*\](.*?)\[\/component\]/g,
      '<span class="wiring-component-link" title="$1:$2">$3</span>'
    );
  }

  isAnswerActive ( level : number, answer : 'yes' | 'no' ) : boolean {
    if (level >= this.stepAnswerChain.length) {
      return false;
    }

    const parentStep = level === 0
      ? this.steps[this.expandedStepIndex]
      : this.stepAnswerChain[level - 1];

    if (!parentStep) {
      return false;
    }

    return answer === 'yes'
      ? this.stepAnswerChain[level] === parentStep.yes_answer
      : this.stepAnswerChain[level] === parentStep.no_answer;
  }

  isStepExpanded ( index : number ) : boolean {
    return this.expandedStepIndex === index;
  }

  navigateToComponent ( component : HaynesProVesaPinConnectionComponentModel ) : void {
    if (component?.component_id && component?.system_type_id) {
      this.componentNavigate.emit({
        componentId : component.component_id,
        systemTypeId: component.system_type_id
      });
    }
  }

  onPinRowClick = ( event : TableRowEvent ) : void => {
    const row = event.row as HaynesProVesaPinConnectionRowModel;

    if (row?.__component) {
      this.navigateToComponent(row.__component);
    }
  };

  onPinTableMouseLeave () : void {
    this.clearSvgHighlight();
  }

  onPinTableMouseOver ( event : MouseEvent ) : void {
    const tr = (event.target as Element)?.closest('tr');

    if (!tr || !tr.parentElement) {
      return;
    }

    // Ignore hover over the header row
    if (tr.parentElement.tagName.toLowerCase() !== 'tbody') {
      return;
    }

    const rows  = Array.from(tr.parentElement.children);
    const index = rows.indexOf(tr);

    if (index < 0 || index >= this.pinTableRows.length) {
      return;
    }

    const row         = this.pinTableRows[index];
    const componentId = row?.__component?.component_id;

    if (componentId && componentId !== this.highlightedCode) {
      this.applySvgHighlight(componentId);
      this.highlightedCode = componentId;
    }
  }

  onStepTextClick ( event : MouseEvent ) : void {
    const target = event.target as HTMLElement;

    if (!target.classList.contains('wiring-component-link')) {
      return;
    }

    const title = target.getAttribute('title') || '';
    const match = title.match(/^(\d+):(\d+)$/);

    if (match) {
      this.componentNavigate.emit({
        componentId : match[1],
        systemTypeId: match[2]
      });
    }
  }

  resetDiagram () : void {
    this.clearStepOverlay();
    this.clearWireTracing();
    this.expandedStepIndex = -1;
    this.panX              = 0;
    this.panY              = 0;
    this.zoomLevel         = 1;
    this.isExtendedView    = false;
    this.loadWiringSvg();
  }

  showConciseDiagram () : void {
    this.isExtendedView = false;
    this.panX           = 0;
    this.panY           = 0;
    this.zoomLevel      = 1;
    this.loadWiringSvg();
  }

  showExtendedDiagram () : void {
    if (this.images?.wiring?.extended) {
      this.clearStepOverlay();
      this.expandedStepIndex = -1;
      this.stepAnswerChain   = [];
      this.isExtendedView    = true;
      this.panX              = 0;
      this.panY              = 0;
      this.zoomLevel         = 1;
      this.loadWiringSvg();
    }
  }

  toggleDiagnosisPanel () : void {
    this.diagnosisPanelOpen = !this.diagnosisPanelOpen;
  }

  toggleStep ( index : number ) : void {
    const wasExpanded = this.expandedStepIndex === index;

    this.expandedStepIndex = wasExpanded ? -1 : index;
    this.stepAnswerChain   = [];

    // Clear any existing tool overlay
    this.clearStepOverlay();

    // Show overlay for newly expanded step
    if (!wasExpanded && index >= 0 && index < this.steps.length) {
      this.showStepOverlay(this.steps[index]);
    }
  }

  zoomIn () : void {
    const next = this.getNextZoomLevel(1);

    if (next !== null) {
      this.zoomToPoint(next);
    }
  }

  zoomOut () : void {
    const next = this.getNextZoomLevel(-1);

    if (next !== null) {
      this.zoomToPoint(next);
    }
  }

  private applySvgHighlight ( componentId : string ) : void {
    this.clearSvgHighlight();

    const anchor = this.svgComponentAnchors.get(componentId);

    if (!anchor) {
      return;
    }

    // Highlight the anchor element (image/rect box) itself
    anchor.classList.add('wiring-svg-anchor-highlighted');
  }


  private bindComponentClicks () : void {
    const container = this.svgContainer?.nativeElement;
    const svg       = container?.querySelector('svg');

    if (!svg) {
      return;
    }

    // Reset maps
    this.svgComponentAnchors.clear();
    this.svgCodeToAnchorData.clear();

    const anchors = Array.from(svg.querySelectorAll('[id^="ecomponent"], [id^="controlunit"]'));

    anchors.forEach(( anchor : Element ) => {
      const id          = anchor.getAttribute('id')          || '';
      const dataSearch  = anchor.getAttribute('data-search') || '';
      const description = anchor.getAttribute('description') || '';

      // Extract component_id from "ecomponent{id}" or "controlunit{id}"
      const idMatch = id.match(/^(?:ecomponent|controlunit)(\d+)/);

      if (!idMatch) {
        return;
      }

      const componentId = idMatch[1];
      const code        = dataSearch.split(/\s+/)[0] || '';

      this.svgComponentAnchors.set(componentId, anchor);

      // Build tooltip text
      const tooltipText = code && description
        ? code + ' — ' + description
        : code || description;

      if (tooltipText) {
        anchor.setAttribute('data-tooltip', tooltipText);
      }

      // Index by code for text-content fallback in event handlers
      if (code) {
        this.svgCodeToAnchorData.set(code, { componentId, tooltipText });
      }

      (anchor as SVGElement).style.cursor = 'pointer';
      anchor.classList.add('wiring-svg-clickable-group');
      anchor.setAttribute('data-component-id', componentId);
    });

    // Tag text labels with cursor styling where possible
    this.tagAllComponentText(svg);

    // Set up custom instant tooltip on the SVG container
    this.bindInstantTooltip(svg);

    // SVG-level click — find the component_id of the clicked element
    svg.addEventListener('click', ( event : Event ) => {
      if (this.isDragging) {
        return;
      }

      const target      = event.target as Element;
      let componentId = this.resolveClickedComponentId(target, svg);

      if (!componentId) {
        componentId = this.resolveTextComponentId(target);
      }

      if (!componentId) {
        return;
      }

      const component = this.findComponentById(componentId);

      if (component) {
        event.stopPropagation();
        this.navigateToComponent(component);
      }
    });
  }


  private bindDragPan () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport || this.dragMouseDownBound) {
      return;
    }

    this.dragMouseDownBound = ( event : MouseEvent ) => {
      // Only left mouse button
      if (event.button !== 0) {
        return;
      }

      this.isDragging     = false;
      this.dragStartX     = event.clientX;
      this.dragStartY     = event.clientY;
      this.dragScrollLeft = this.panX;
      this.dragScrollTop  = this.panY;

      document.addEventListener('mousemove', this.dragMouseMoveBound!);
      document.addEventListener('mouseup', this.dragMouseUpBound!);
    };

    this.dragMouseMoveBound = ( event : MouseEvent ) => {
      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;

      // Only start dragging once movement exceeds threshold (preserves clicks)
      if (!this.isDragging && (Math.abs(dx) > this.DRAG_THRESHOLD || Math.abs(dy) > this.DRAG_THRESHOLD)) {
        this.isDragging       = true;
        viewport.style.cursor = 'grabbing';
      }

      if (this.isDragging) {
        event.preventDefault();
        this.dragLatestX = event.clientX;
        this.dragLatestY = event.clientY;

        if (this.dragRafId === null) {
          this.dragRafId = requestAnimationFrame(() => {
            const moveX = this.dragLatestX - this.dragStartX;
            const moveY = this.dragLatestY - this.dragStartY;

            this.panX = this.dragScrollLeft + moveX;
            this.panY = this.dragScrollTop  + moveY;

            this.updateStageTransform();

            this.dragRafId = null;
          });
        }
      }
    };

    this.dragMouseUpBound = ( event : MouseEvent ) => {
      if (this.isDragging) {
        // Suppress click events that would otherwise fire on SVG components
        event.preventDefault();
        event.stopPropagation();
      }

      this.isDragging       = false;
      viewport.style.cursor = '';

      if (this.dragRafId !== null) {
        cancelAnimationFrame(this.dragRafId);
        this.dragRafId = null;
      }

      document.removeEventListener('mousemove', this.dragMouseMoveBound!);
      document.removeEventListener('mouseup', this.dragMouseUpBound!);
    };

    viewport.addEventListener('mousedown', this.dragMouseDownBound);
  }


  private bindInstantTooltip ( svg : Element ) : void {
    if (this.tooltipMouseOver) {
      return;
    }

    this.tooltipMouseOver = ( event : MouseEvent ) => {
      const target  = event.target as Element;
      const el      = target?.closest?.('[data-tooltip]');
      let tooltip = el?.getAttribute('data-tooltip') || null;

      // Fallback: resolve tooltip from text content against known component codes
      if (!tooltip) {
        const data = this.resolveTextAnchorData(target);
        tooltip    = data?.tooltipText || null;
      }

      if (tooltip) {
        this.showTooltip(tooltip, event.clientX, event.clientY);
      }
    };

    this.tooltipMouseOut = ( event : MouseEvent ) => {
      const from = event.target as Element;
      const to   = event.relatedTarget as Element | null;

      const fromHas = from?.closest?.('[data-tooltip]') || this.resolveTextAnchorData(from);
      const toHas   = to?.closest?.('[data-tooltip]')   || this.resolveTextAnchorData(to);

      if (fromHas && !toHas) {
        this.hideTooltip();
      }
    };

    this.tooltipMouseMove = ( event : MouseEvent ) => {
      if (this.tooltipEl && this.tooltipEl.style.display !== 'none') {
        this.positionTooltip(event.clientX, event.clientY);
      }
    };

    svg.addEventListener('mouseover', this.tooltipMouseOver as EventListener);
    svg.addEventListener('mouseout',  this.tooltipMouseOut  as EventListener);
    svg.addEventListener('mousemove', this.tooltipMouseMove as EventListener);
  }


  private bindWheelZoom () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport || this.wheelListenerBound) {
      return;
    }

    this.wheelListenerBound = ( event : WheelEvent ) => {
      // Only zoom when Ctrl is not pressed (Ctrl+wheel is browser zoom)
      if (event.ctrlKey) {
        return;
      }

      event.preventDefault();

      const next = event.deltaY < 0
        ? this.getNextZoomLevel(1)
        : this.getNextZoomLevel(-1);

      if (next !== null) {
        const rect   = viewport.getBoundingClientRect();
        const focalX = event.clientX - rect.left;
        const focalY = event.clientY - rect.top;

        this.zoomToPoint(next, focalX, focalY);
      }
    };

    viewport.addEventListener('wheel', this.wheelListenerBound, { passive: false });
  }


  private bindWireHover () : void {
    const svg = this.svgContainer?.nativeElement?.querySelector('svg');

    if (!svg) {
      return;
    }

    svg.addEventListener('mouseover', (( event : Event ) => {
      const target    = event.target as Element;
      const wireGroup = target.closest('[id^="wireGroup"]');

      if (!wireGroup || wireGroup.classList.contains('wire-traced')) {
        return;
      }

      wireGroup.classList.add('wire-traced');

      wireGroup.querySelectorAll('line:not([data-hover])')
      .forEach(( line : Element ) => {
        (line as SVGElement).style.stroke = 'red';
      });
    }) as EventListener);
  }


  private buildComponentCodeMap () : void {
    this.componentCodeMap.clear();

    if (!this.pinConnections?.length) {
      return;
    }

    this.pinConnections.forEach(pin => {
      if (pin.component?.code && !this.componentCodeMap.has(pin.component.code)) {
        this.componentCodeMap.set(pin.component.code, pin.component);
      }
    });
  }


  private buildPinTableRows () : void {
    if (!this.pinConnections?.length) {
      this.pinTableRows = [];

      return;
    }

    this.pinTableRows = this.pinConnections.map(pin => ({
      __component : pin.component,
      component   : ((pin.component?.code || '') + ' ' + (pin.component?.name || '')).trim(),
      pin_name    : pin.pin?.name || '',
      pin_number  : pin.pin?.number || '',
      wire_color_1: pin.wire_colors?.[0] || '',
      wire_color_2: pin.wire_colors?.[1] || ''
    }));
  }


  private buildPinTableSettings () : void {
    const pinHeader  = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.PIN_NUMBER');
    const wireHeader = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.WIRE_COLOUR_CODE');
    const compHeader = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.COMPONENT');

    this.pinTableSettings = {
      columns: [
        { field: 'pin_number',   header: { text: pinHeader  }, classes: 'pin-number-col' },
        { field: 'wire_color_1', header: { text: wireHeader } },
        { field: 'component',    header: { text: compHeader }, classes: 'pin-component-col' },
        { field: 'pin_name',     header: { text: pinHeader  } },
        { field: 'wire_color_2', header: { text: wireHeader } }
      ],
      empty: {
        text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.NO_DATA')
      },
      rows: {
        highlight: { enabled: true }
      },
      sorting: { enabled: false }
    };
  }


  private clearStepOverlay () : void {
    const svg = this.svgContainer?.nativeElement?.querySelector('svg') as SVGSVGElement;

    if (!svg) {
      return;
    }

    const overlay = svg.querySelector('.wiring-multimeter-overlay');

    if (overlay) {
      overlay.remove();
      svg.removeAttribute('overflow');

      // Restore original viewBox
      if (this.originalSvgViewBox) {
        svg.setAttribute('viewBox', this.originalSvgViewBox);
        this.originalSvgViewBox = null;

        setTimeout(() => this.computeSvgBaseDimensions());
      }
    }
  }


  private clearSvgHighlight () : void {
    this.highlightedCode = null;

    const svg = this.svgContainer?.nativeElement?.querySelector('svg');

    if (!svg) {
      return;
    }

    svg.querySelectorAll('.wiring-svg-anchor-highlighted').forEach(( el : Element ) => {
      el.classList.remove('wiring-svg-anchor-highlighted');
    });
  }


  private clearWireTracing () : void {
    const svg = this.svgContainer?.nativeElement?.querySelector('svg');

    if (!svg) {
      return;
    }

    svg.querySelectorAll('.wire-traced').forEach(( group : Element ) => {
      group.classList.remove('wire-traced');

      group.querySelectorAll('line:not([data-hover])').forEach(( line : Element ) => {
        (line as SVGElement).style.stroke = '';
      });
    });
  }


  private computeSvgBaseDimensions () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport) {
      return;
    }

    this.svgBaseWidth  = viewport.clientWidth;
    this.svgBaseHeight = viewport.clientHeight;
  }


  private deriveModeLabel ( hint : string ) : string {
    if (/Ω|ohm|resist/i.test(hint)) {
      return 'Ω';
    }

    if (/V|volt/i.test(hint)) {
      return 'V';
    }

    if (/A|amp|current/i.test(hint)) {
      return 'A';
    }

    return 'Ω';
  }


  private ensureSvgOverflowVisible () : void {
    const svg = this.svgContainer?.nativeElement?.querySelector('svg');

    if (svg) {
      svg.setAttribute('overflow', 'visible');
    }
  }


  private ensureSvgViewBox ( svgText : string ) : string {
    if (/viewBox\s*=/i.test(svgText)) {
      return svgText;
    }

    const widthMatch  = svgText.match(/<svg[^>]*\bwidth\s*=\s*["']?(\d+(?:\.\d+)?)/i);
    const heightMatch = svgText.match(/<svg[^>]*\bheight\s*=\s*["']?(\d+(?:\.\d+)?)/i);

    if (widthMatch && heightMatch) {
      return svgText.replace(/<svg([^>]*)>/i, `<svg$1 viewBox="0 0 ${widthMatch[1]} ${heightMatch[1]}">`);
    }

    return svgText;
  }


  private findComponentById ( componentId : string ) : HaynesProVesaPinConnectionComponentModel | null {
    if (!this.pinConnections?.length) {
      return null;
    }

    const match = this.pinConnections.find(p => p.component?.component_id === componentId);

    return match?.component || null;
  }


  private fitSvgViewBoxToContent () : void {
    const svg = this.svgContainer?.nativeElement?.querySelector('svg') as SVGSVGElement;

    if (!svg) {
      return;
    }

    const contentGroup = svg.querySelector('g') as SVGGraphicsElement;

    if (!contentGroup) {
      return;
    }

    try {
      const bbox    = contentGroup.getBBox();
      const vb      = svg.viewBox?.baseVal;
      const padding = 10;

      // Current viewBox bounds
      const vbX = vb?.width ? vb.x : 0;
      const vbY = vb?.width ? vb.y : 0;
      const vbR = vb?.width ? vb.x + vb.width  : bbox.x + bbox.width;
      const vbB = vb?.width ? vb.y + vb.height : bbox.y + bbox.height;

      // Expand if content exceeds current viewBox
      const newX = Math.min(vbX, bbox.x - padding);
      const newY = Math.min(vbY, bbox.y - padding);
      const newR = Math.max(vbR, bbox.x + bbox.width  + padding);
      const newB = Math.max(vbB, bbox.y + bbox.height + padding);

      if (newX < vbX || newY < vbY || newR > vbR || newB > vbB) {
        svg.setAttribute('viewBox', `${newX} ${newY} ${newR - newX} ${newB - newY}`);
      }
    } catch (_e) {}
  }


  private getNextZoomLevel ( direction : number ) : number | null {
    if (direction > 0) {
      const next = this.ZOOM_STEPS.find(( step : number ) => step > this.zoomLevel);

      return next !== undefined ? next : null;
    }

    const prev = this.ZOOM_STEPS.filter(( step : number ) => step < this.zoomLevel);

    return prev.length ? prev[prev.length - 1] : null;
  }


  private hideTooltip () : void {
    if (this.tooltipEl) {
      this.tooltipEl.style.display = 'none';
    }
  }


  private init () : void {
    this.buildComponentCodeMap();
    this.buildPinTableRows();
    this.buildPinTableSettings();

    if (this.diagnosisGroups?.length) {
      this.steps = this.diagnosisGroups[this.currentGroupIndex]?.steps || [];
    } else {
      this.steps = [];
    }

    this.expandedStepIndex = -1;
    this.panX              = 0;
    this.panY              = 0;
    this.zoomLevel         = 1;
    this.isExtendedView    = false;
  }

  private async loadSvgFromUrl ( url : string | undefined ) : Promise<void> {
    if (!url || !this.svgContainer?.nativeElement) {
      return;
    }

    this.loadingSvg = true;
    this.svgError   = '';

    try {
      const response = await fetch(url, { mode: 'cors' });

      if (!response.ok) {
        throw new Error('Failed to load wiring diagram.');
      }

      const svgText      = await response.text();
      const processedSvg = this.ensureSvgViewBox(svgText);

      this.injectInlineSvg(processedSvg, this.svgContainer);
      this.removeInternalClipPaths();
      this.ensureSvgOverflowVisible();
      this.bindComponentClicks();
      this.bindWireHover();
      this.fitSvgViewBoxToContent();

      setTimeout(() => this.computeSvgBaseDimensions());
    } catch (err) {
      this.svgError = (err as any)?.message || 'Unable to load wiring diagram.';
    } finally {
      this.loadingSvg = false;
    }
  }


  private loadWiringSvg () : void {
    const url = this.isExtendedView
      ? this.images?.wiring?.extended
      : this.images?.wiring?.concise;

    this.loadSvgFromUrl(url);
  }


  private parseStepToolData ( step : HaynesProVesaDiagnosisStepModel )
    : { displayValue: string; modeLabel: string; pins: Array<{ pinNameId: string }> } | null {

    // Prefer structured tool data
    if (step.tool?.pins?.length) {
      const value = ((step.tool.value || '') + ' ' + (step.tool.unit || '')).trim();

      return {
        displayValue: value || '—',
        modeLabel   : this.deriveModeLabel(step.tool.unit || step.tool.state || ''),
        pins        : step.tool.pins.map(p => ({ pinNameId: p.pin_name_id }))
      };
    }

    if (step.flash_vars) {
      const parts = step.flash_vars.split(',');

      if (parts.length < 4) {
        return null;
      }

      const displayValue = parts[2]?.trim() || '—';
      const pins : Array<{ pinNameId: string }> = [];

      for (let i = 3; i < parts.length; i++) {
        const tokens = parts[i].trim().split(/\s+/);

        if (tokens.length >= 2) {
          pins.push({ pinNameId: tokens[1] });
        }
      }

      return {
        displayValue,
        modeLabel: this.deriveModeLabel(displayValue),
        pins
      };
    }

    return null;
  }


  private positionTooltip ( x : number, y : number ) : void {
    if (!this.tooltipEl) {
      return;
    }

    this.tooltipEl.style.left = (x + 12) + 'px';
    this.tooltipEl.style.top  = (y + 12) + 'px';
  }


  private removeInternalClipPaths () : void {
    const svg = this.svgContainer?.nativeElement?.querySelector('svg');

    if (!svg) {
      return;
    }

    // HaynesPro SVGs have clipPath elements that clip content to a fixed
    // rectangle, cutting off components near the edges. Remove them so
    // the full diagram is visible — our CSS container handles clipping.
    svg.querySelectorAll('[clip-path]').forEach(( el : Element ) => {
      el.removeAttribute('clip-path');
    });
  }


  private resolveClickedComponentId ( target : Element, svg : Element ) : string | null {
    let el : Element | null = target;

    while (el && el !== svg) {
      // Check data-component-id first (set on text labels and clickable groups)
      const dataId = el.getAttribute?.('data-component-id');

      if (dataId) {
        return dataId;
      }

      // Then check native SVG id pattern on anchors
      const id    = el.getAttribute?.('id') || '';
      const match = id.match(/^(?:ecomponent|controlunit)(\d+)/);

      if (match) {
        return match[1];
      }

      el = el.parentElement;
    }

    return null;
  }


  private resolveTextAnchorData ( target : Element | null ) : { componentId: string; tooltipText: string } | null {
    if (!target) {
      return null;
    }

    const tag = target.tagName?.toLowerCase();

    if (tag !== 'text' && tag !== 'tspan') {
      return null;
    }

    const textEl  = tag === 'tspan' ? (target.closest('text') || target) : target;
    const content = (textEl.textContent || '').trim();

    return this.svgCodeToAnchorData.get(content) || null;
  }


  private resolveTextComponentId ( target : Element ) : string | null {
    // Resolve component id from text content — handles <text> and <tspan>
    const tag = target?.tagName?.toLowerCase();

    if (tag !== 'text' && tag !== 'tspan') {
      return null;
    }

    const textEl  = tag === 'tspan' ? (target.closest('text') || target) : target;
    const content = (textEl.textContent || '').trim();
    const data    = this.svgCodeToAnchorData.get(content);

    return data?.componentId || null;
  }


  private showStepOverlay ( step : HaynesProVesaDiagnosisStepModel ) : void {
    const toolData = this.parseStepToolData(step);

    if (!toolData?.pins?.length) {
      return;
    }

    const svg = this.svgContainer?.nativeElement?.querySelector('svg') as SVGSVGElement;

    if (!svg) {
      return;
    }

    // Append overlay to the SVG root so coordinates match SVG viewBox space
    const contentGroup = svg.querySelector('g') as SVGGraphicsElement;

    if (!contentGroup) {
      return;
    }

    const groupCTM = contentGroup.getScreenCTM();

    if (!groupCTM) {
      return;
    }

    const groupCTMInv = groupCTM.inverse();

    // Locate pin elements and convert to content group coordinate space
    const pinPositions : Array<{ x: number; y: number }> = [];

    toolData.pins.forEach(pin => {
      const el = svg.querySelector('#pin' + pin.pinNameId) as SVGGraphicsElement;

      if (!el) {
        return;
      }

      try {
        const elCTM = el.getScreenCTM();

        if (!elCTM) {
          return;
        }

        const bbox = el.getBBox();
        const pt   = svg.createSVGPoint();

        pt.x = bbox.x + bbox.width  / 2;
        pt.y = bbox.y + bbox.height / 2;

        const groupPt = pt.matrixTransform(elCTM).matrixTransform(groupCTMInv);

        pinPositions.push({ x: groupPt.x, y: groupPt.y });
      } catch (_e) {}
    });

    if (!pinPositions.length) {
      return;
    }

    // ── HaynesPro reference dimensions ──

    const ns    = 'http://www.w3.org/2000/svg';
    const xlink = 'http://www.w3.org/1999/xlink';
    const g     = document.createElementNS(ns, 'g');

    // Multimeter positioning
    g.setAttribute('class', 'wiring-multimeter-overlay');

    const meterW = 108;
    const meterH = 190;
    const penW   = 36;
    const penH   = 70;

    // ── Position meter: AFTER the entire SVG content (to the far right) ──
    const minY = Math.min(...pinPositions.map(p => p.y));

    let contentRight = Math.max(...pinPositions.map(p => p.x)) + 100;

    try {
      const cb = contentGroup.getBBox();

      contentRight = cb.x + cb.width;
    } catch (_e) {}

    const meterX = contentRight + 50;
    const meterY = minY - meterH * 0.5;
    const conY   = meterY + meterH;

    // ── Multimeter image ──
    const meterImg = document.createElementNS(ns, 'image');

    meterImg.setAttribute('x', String(meterX));
    meterImg.setAttribute('y', String(meterY));
    meterImg.setAttribute('width', String(meterW));
    meterImg.setAttribute('height', String(meterH));
    meterImg.setAttributeNS(xlink, 'href', this.VESA_ASSETS + 'multimeter.svg');
    meterImg.setAttribute('preserveAspectRatio', 'none');
    meterImg.setAttribute('pointer-events', 'none');
    g.appendChild(meterImg);

    // ── Display value text (on the meter's LCD screen) ──
    g.appendChild(this.svgEl(ns, 'text', {
      x               : String(meterX + meterW * 0.76),
      y               : String(meterY + meterH * 0.3),
      'text-anchor'   : 'end',
      'font-family'   : 'Arial, sans-serif',
      'font-size'     : '12',
      'font-weight'   : 'bold',
      fill            : '#333',
      stroke          : 'none',
      'pointer-events': 'none'
    }, toolData.displayValue));

    // ── Meter terminal positions ──
    const redConX   = meterX + meterW * 0.75;
    const blackConX = meterX + meterW * 0.57;

    // ── Build wire targets ──
    const groundW = 20;
    const groundH = 18;
    let groundTarget : { x: number; y: number } | null = null;

    if (pinPositions.length === 1) {
      // Ground hangs straight below the meter (set after meter is positioned)
      groundTarget = { x: 0, y: 0 };
    }

    const wireTargets = [
      pinPositions[0],
      pinPositions.length > 1 ? pinPositions[1] : groundTarget!
    ];

    // ── Probe wires + pen images ──
    const wireConfigs = [
      { color: '#b56348', conX: redConX,   penAsset: 'meetpen_rood.png' },
      { color: '#66686a', conX: blackConX, penAsset: 'meetpen_zwart.png' }
    ];

    wireTargets.forEach(( pos, i ) => {
      if (!pos || i >= wireConfigs.length) {
        return;
      }

      const cfg       = wireConfigs[i];
      const isGround  = i === 1 && !!groundTarget;
      const startX    = cfg.conX;
      const startY    = conY;

      if (isGround) {
        // ── Ground: pen hangs to the RIGHT of the meter(only when grounding) ──
        const gndPenX = meterX + meterW + 40;
        const gndPenY = startY + 30;

        // Wire curves from the black terminal right and down to the pen
        g.appendChild(this.svgEl(ns, 'path', {
          d               : `M ${startX} ${startY} C ${startX} ${startY + 30}, ${gndPenX} ${startY}, ${gndPenX} ${gndPenY + 5}`,
          fill            : 'none',
          stroke          : cfg.color,
          'stroke-width'  : '3',
          'pointer-events': 'none'
        }));

        const penImg = document.createElementNS(ns, 'image');

        penImg.setAttribute('x', String(gndPenX - penW / 2));
        penImg.setAttribute('y', String(gndPenY));
        penImg.setAttribute('width', String(penW));
        penImg.setAttribute('height', String(penH));
        penImg.setAttributeNS(xlink, 'href', this.VESA_ASSETS + cfg.penAsset);
        penImg.setAttribute('transform', `rotate(180,${gndPenX},${gndPenY + penH / 2})`);
        penImg.setAttribute('pointer-events', 'none');
        g.appendChild(penImg);

        const gndTipY = gndPenY + penH;
        const gndImg  = document.createElementNS(ns, 'image');

        gndImg.setAttribute('x', String(gndPenX - groundW / 2));
        gndImg.setAttribute('y', String(gndTipY));
        gndImg.setAttribute('width', String(groundW));
        gndImg.setAttribute('height', String(groundH));
        gndImg.setAttributeNS(xlink, 'href', this.VESA_ASSETS + 'ground.png');
        gndImg.setAttribute('pointer-events', 'none');
        g.appendChild(gndImg);
      } else {
        // ── Pin wire: full drooping bezier to pin position ──
        const wireEndX = pos.x + 42;
        const wireEndY = pos.y + 98;

        const dx    = startX - wireEndX;
        const endDy = wireEndY - startY;
        const dropH = meterH + Math.max(0, endDy);

        // Part 1: bezier from terminal -> droops down -> curves to wire end
        g.appendChild(this.svgEl(ns, 'path', {
          d               : `M ${startX} ${startY} c 0 ${dropH}, ${-dx} ${dropH}, ${-dx} ${endDy}`,
          fill            : 'none',
          stroke          : cfg.color,
          'stroke-width'  : '3',
          'pointer-events': 'none'
        }));

        // Part 2: short segment angling UP toward pen handle
        g.appendChild(this.svgEl(ns, 'path', {
          d               : `M ${wireEndX} ${wireEndY} c 0 -20, 0 -20, -10 -40`,
          fill            : 'none',
          stroke          : cfg.color,
          'stroke-width'  : '3',
          'pointer-events': 'none'
        }));

        // Probe pen image: tip at pin, rotated -30°
        const penImg = document.createElementNS(ns, 'image');

        penImg.setAttribute('x', String(pos.x - penW / 2));
        penImg.setAttribute('y', String(pos.y));
        penImg.setAttribute('width', String(penW));
        penImg.setAttribute('height', String(penH));
        penImg.setAttributeNS(xlink, 'href', this.VESA_ASSETS + cfg.penAsset);
        penImg.setAttribute('transform', `rotate(-30,${pos.x},${pos.y})`);
        penImg.setAttribute('pointer-events', 'none');
        g.appendChild(penImg);
      }
    });

    contentGroup.appendChild(g);

    // Expand the SVG viewBox to fit the overlay (wires droop below the diagram)
    this.originalSvgViewBox = svg.getAttribute('viewBox');

    try {
      const bbox    = contentGroup.getBBox();
      const padding = 20;

      svg.setAttribute('viewBox',
        `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
      );

      setTimeout(() => this.computeSvgBaseDimensions());
    } catch (_e) {
      // Fallback: allow overflow if getBBox fails
      svg.setAttribute('overflow', 'visible');
    }
  }


  private showTooltip ( text : string, x : number, y : number ) : void {
    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'wiring-tooltip-floater';
      document.body.appendChild(this.tooltipEl);
    }

    this.tooltipEl.textContent   = text;
    this.tooltipEl.style.display = 'block';
    this.positionTooltip(x, y);
  }


  private svgEl ( ns : string, tag : string, attrs : Record<string, string>, text ?: string ) : Element {
    const el = document.createElementNS(ns, tag);

    Object.entries(attrs).forEach(([ key, val ]) => el.setAttribute(key, val));

    if (text !== undefined) {
      el.textContent = text;
    }

    return el;
  }


  private tagAllComponentText ( svg : Element ) : void {
    const texts = Array.from(svg.querySelectorAll('text'));

    texts.forEach(( textEl : Element ) => {
      if (textEl.getAttribute('data-component-id')) {
        return;
      }

      const content = (textEl.textContent || '').trim();
      const data    = this.svgCodeToAnchorData.get(content);

      if (data) {
        textEl.setAttribute('data-tooltip', data.tooltipText);
        textEl.setAttribute('data-component-id', data.componentId);
        (textEl as SVGElement).style.cursor = 'pointer';
        textEl.classList.add('wiring-svg-clickable-group');
      }
    });
  }


  private updateStageTransform () : void {
    const stage = this.svgContainer?.nativeElement;

    if (stage) {
      stage.style.transform = this.stageTransform;
    }
  }


  private zoomToPoint ( newZoom : number, focalX ?: number, focalY ?: number ) : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport) {
      this.zoomLevel = newZoom;

      return;
    }

    const oldZoom = this.zoomLevel;

    // Default focal point: center of viewport (used by +/− buttons)
    const fx = focalX !== undefined ? focalX : viewport.clientWidth  / 2;
    const fy = focalY !== undefined ? focalY : viewport.clientHeight / 2;

    // Adjust pan so the content point under the focal stays fixed
    this.panX = fx - (fx - this.panX) * newZoom / oldZoom;
    this.panY = fy - (fy - this.panY) * newZoom / oldZoom;

    this.zoomLevel = newZoom;
    this.updateStageTransform();
  }

}
